import {
  useMemo,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";
import {
  GRID_COLS,
  MIN_CARD_H,
  MIN_CARD_W,
  rectanglesOverlap,
  type GridItem,
  type NewGridItem,
} from "../store/workspace.js";

const ROW_HEIGHT = 40;
const GAP = 16;

interface Cell {
  col: number;
  row: number;
}

interface GridPlacementLayerProps {
  layout: GridItem[];
  rows: number;
  onSelect: (item: NewGridItem) => void;
}

export function GridPlacementLayer({
  layout,
  rows,
  onSelect,
}: GridPlacementLayerProps) {
  const [anchor, setAnchor] = useState<Cell | null>(null);
  const [current, setCurrent] = useState<Cell | null>(null);
  const [dragging, setDragging] = useState(false);

  const selection = useMemo(
    () => makeSelection(anchor, current),
    [anchor, current],
  );
  const selectionIsLargeEnough =
    selection !== null &&
    selection.w >= MIN_CARD_W &&
    selection.h >= MIN_CARD_H;
  const selectionIsBlocked =
    selection !== null &&
    layout.some((item) => rectanglesOverlap(selection, item));
  const selectionIsValid = selectionIsLargeEnough && !selectionIsBlocked;

  const cellClasses = (col: number, row: number) => {
    const occupied = layout.some(
      (item) =>
        col >= item.x &&
        col < item.x + item.w &&
        row >= item.y &&
        row < item.y + item.h,
    );
    const selected =
      selection !== null &&
      col >= selection.x &&
      col < selection.x + selection.w &&
      row >= selection.y &&
      row < selection.y + selection.h;
    return [
      "placement-grid__cell",
      occupied ? "placement-grid__cell--occupied" : "",
      !occupied ? "placement-grid__cell--available" : "",
      selected ? "placement-grid__cell--selected" : "",
      selected && selectionIsValid ? "placement-grid__cell--valid" : "",
      selected && selectionIsBlocked ? "placement-grid__cell--blocked" : "",
    ]
      .filter(Boolean)
      .join(" ");
  };

  const eventCell = (
    event: ReactPointerEvent<HTMLDivElement>,
  ): Cell => {
    const rect = event.currentTarget.getBoundingClientRect();
    const colWidth = (rect.width - GAP * (GRID_COLS - 1)) / GRID_COLS;
    const col = Math.floor(
      Math.max(
        0,
        Math.min(
          GRID_COLS - 1,
          (event.clientX - rect.left + GAP / 2) / (colWidth + GAP),
        ),
      ),
    );
    const row = Math.floor(
      Math.max(
        0,
        Math.min(
          rows - 1,
          (event.clientY - rect.top + GAP / 2) / (ROW_HEIGHT + GAP),
        ),
      ),
    );
    return { col, row };
  };

  const handlePointerDown = (
    event: ReactPointerEvent<HTMLDivElement>,
  ) => {
    if (event.button !== 0) return;
    const cell = eventCell(event);
    event.currentTarget.setPointerCapture(event.pointerId);
    setAnchor(cell);
    setCurrent(cell);
    setDragging(true);
  };

  const handlePointerMove = (
    event: ReactPointerEvent<HTMLDivElement>,
  ) => {
    const cell = eventCell(event);
    if (dragging) setCurrent(cell);
  };

  const handlePointerUp = (
    event: ReactPointerEvent<HTMLDivElement>,
  ) => {
    const finalSelection = makeSelection(anchor, eventCell(event));
    const finalSelectionIsLargeEnough =
      finalSelection !== null &&
      finalSelection.w >= MIN_CARD_W &&
      finalSelection.h >= MIN_CARD_H;
    const finalSelectionIsBlocked =
      finalSelection !== null &&
      layout.some((item) => rectanglesOverlap(finalSelection, item));
    setDragging(false);
    if (
      finalSelection &&
      finalSelectionIsLargeEnough &&
      !finalSelectionIsBlocked
    ) {
      onSelect(finalSelection);
    }
    setAnchor(null);
    setCurrent(null);
  };

  return (
    <div className="placement-layer">
      {selection && (
        <div
          className={`placement-size ${
            selectionIsValid
              ? "placement-size--valid"
              : selectionIsBlocked
                ? "placement-size--blocked"
                : ""
          }`}
          aria-live="polite"
        >
          {selection.w} × {selection.h}
          {!selectionIsLargeEnough && ` · ${MIN_CARD_W} × ${MIN_CARD_H} minimum`}
          {selectionIsValid && " · release to add"}
          {selectionIsBlocked && " · area occupied"}
        </div>
      )}

      <div
        className="placement-grid"
        style={{ gridTemplateRows: `repeat(${rows}, ${ROW_HEIGHT}px)` }}
        role="grid"
        aria-label="Choose a chart size and position"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={() => {
          setDragging(false);
          setAnchor(null);
          setCurrent(null);
        }}
      >
        {Array.from({ length: rows * GRID_COLS }, (_, index) => {
          const col = index % GRID_COLS;
          const row = Math.floor(index / GRID_COLS);
          return (
            <div
              key={`${col}-${row}`}
              className={cellClasses(col, row)}
              role="gridcell"
              aria-hidden
            />
          );
        })}
      </div>
    </div>
  );
}

function makeSelection(
  start: Cell | null,
  end: Cell | null,
): NewGridItem | null {
  if (!start || !end) return null;

  return {
    x: Math.min(start.col, end.col),
    y: Math.min(start.row, end.row),
    w: Math.abs(end.col - start.col) + 1,
    h: Math.abs(end.row - start.row) + 1,
  };
}
